
import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import HeaderHelpPage from "../../components/HeaderHelpPage";
import { applySortFilter } from "../../utils/tableFunctions";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";
import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import SearchNotFound from "../../components/table/SearchNotFound";
import { Log, sendDataWithPost, approveNoPayoutDay, rejectNoPayoutDay, deleteNoPayoutDay } from "../../services/services";
import CreateDialog from "./CreateDialog";
import ViewDetailDialog from "./ViewDetailDialog";
import { StyledTableRow } from "../../components/TableRow";
import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import { pageNumber, rowsPerPage, NO_PAYOUT_DATE_DAYS, acquirerList } from "../../utils/constants";
import Pagination from "../../components/Pagination";
import moment from "moment";
import { styles } from "../../utils/styles";

const getStatusColor = (status) => {
  if (status === "Pending") return "#ff9800";
  if (status === "Approved") return "#4caf50";
  if (status === "Rejected") return "#f44336";
  return "#666";
};

const TABLE_HEAD = [
  { id: "no_payout_date", label: "Date" },
  { id: "no_payout_day", label: "Day" },
  { id: "acquirer", label: "Acquirer" },
  { id: "description", label: "Description" },
  { id: "status", label: "Status" },
];

const NoPayoutDayPage = (props) => {
  const userRole = props.userRole || [];
  const canCreate = Array.isArray(userRole) && userRole.includes("Add");
  const canEdit = Array.isArray(userRole) && userRole.includes("Edit");
  const canDelete = Array.isArray(userRole) && userRole.includes("Delete");
  const canApproveReject =
    Array.isArray(userRole) && userRole.includes("Approve/Reject");

  const currentYear = moment().year();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("no_payout_date");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState({ year: currentYear, no_payout_day: "" });
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editInitialData, setEditInitialData] = useState(null);

  const initialDefaultValue = {
    year: currentYear,
    no_payout_day: "all",
  };
  const [defaultValue, setDefaultValue] = useState(initialDefaultValue);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    queryPage({ page: 1 }, filter, newOrder);
  };

  const queryPage = (queryParams, params, sortOrder) => {
    const merged = { ...pagination, ...queryParams, ...(params || filter) };
    const currentOrder = sortOrder || order;
    const queryString = new URLSearchParams({
      page: merged.page,
      page_size: merged.page_size,
      order: currentOrder,
    }).toString();
    const url = `/payout/no_payout_day/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, (res) => {
      setTableData(res);
      setLoaded(true);
    }, setLoaded, () => {
      setPagination((prev) => ({ ...prev, ...queryParams }));
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({ page: newPage + 1, page_size: pagination.page_size });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({ page: 1, page_size: event.target.value });
  };

  const filteredList = tableData.results || [];
  const isItemNotFound = (tableData.results || []).length === 0;

  const handleCloseDialog = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenViewDialog(false);
    setOpenDeleteConfirm(false);
    setDeleteConfirmId(null);
    setSelectedId(null);
    setSelectedRecord(null);
    setEditInitialData(null);
    setApproveLoading(false);
    setRejectLoading(false);
    setDeleteLoading(false);
  };

  const handleRowClick = (row) => {
    Log.info(`[Info] - Click row - No Payout Day Id: ${row.id}`);
    setSelectedRecord(row);
    setOpenViewDialog(true);
  };

  const handleViewEdit = (recordOrId) => {
    const isRecord =
      recordOrId != null && typeof recordOrId === "object" && !Array.isArray(recordOrId);
    const id = isRecord ? recordOrId?.id : recordOrId;

    setOpenViewDialog(false);
    setSelectedRecord(null);
    setSelectedId(id ?? null);
    setEditInitialData(isRecord ? recordOrId : null);
    setOpenEditDialog(true);
  };

  const onFilterSubmit = (fParams) => {
    const newPagination = { ...pagination, page: 1 };
    const year = fParams.year != null && fParams.year !== "" ? fParams.year : "";
    const no_payout_day = fParams.no_payout_day && fParams.no_payout_day !== "all" ? fParams.no_payout_day : "";
    const apiParams = { year, no_payout_day };
    Log.info("Click button [Apply Filter] - Search No Payout Day list", apiParams);
    setFilter(apiParams);
    setDefaultValue({
      year: fParams.year != null && fParams.year !== "" ? fParams.year : "",
      no_payout_day: fParams.no_payout_day && fParams.no_payout_day !== "all" ? fParams.no_payout_day : "all",
    });
    queryPage(newPagination, apiParams);
  };

  const schema = [
    {
      type: "year",
      label: "Year",
      param: "year",
    },
    {
      type: "select",
      label: "Day",
      param: "no_payout_day",
      options: [
        { id: "all", name: "All" },
        ...NO_PAYOUT_DATE_DAYS.map((d) => ({ id: d, name: d })),
      ],
    },
  ];

  const handleCreate = () => {
    Log.info("[Info] - Click button [Create No Payout Day] - Open dialog");
    setOpenCreateDialog(true);
  };

  const handleApprove = async (id) => {
    if (approveLoading || rejectLoading) return;
    Log.info(`[Info] - Click button [Approve] - No Payout Day Id: ${id}`);
    setApproveLoading(true);
    try {
      await approveNoPayoutDay(id, () => {
        handleCloseDialog();
        queryPage(pagination, filter);
      });
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async (id) => {
    if (approveLoading || rejectLoading) return;
    Log.info(`[Info] - Click button [Reject] - No Payout Day Id: ${id}`);
    setRejectLoading(true);
    try {
      await rejectNoPayoutDay(id, () => {
        handleCloseDialog();
        queryPage(pagination, filter);
      });
    } finally {
      setRejectLoading(false);
    }
  };

  const handleRequestDelete = (id) => {
    setDeleteConfirmId(id);
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    const id = deleteConfirmId;
    setOpenDeleteConfirm(false);
    setDeleteConfirmId(null);
    if (id) handleDelete(id);
  };

  const handleDelete = async (id) => {
    if (deleteLoading || approveLoading || rejectLoading) return;
    Log.info(`[Info] - Click button [Delete] - No Payout Day Id: ${id}`);
    setDeleteLoading(true);
    try {
      await deleteNoPayoutDay(id, () => {
        handleCloseDialog();
        queryPage(pagination, filter);
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReset = () => {
    setDefaultValue(initialDefaultValue);
    setFormKey((prev) => prev + 1);
  };

  useEffect(() => {
    queryPage(pagination, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <HeaderHelpPage
        title="No Payout Day"
        description="Manage no payout days for payout operations."
      />
    <Box>
      {canCreate && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<Add />}
            sx={{ ...styles.customButton }}
          >
            Create No Payout Day
          </Button>
        </Box>
      )}
      <FormSearch
        key={formKey}
        schema={schema}
        loading={false}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
        onReset={handleReset}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map((row) => {
                    const { id, no_payout_date, no_payout_day, acquirer, description, status } = row;
                    return (
                      <StyledTableRow
                        hover
                        key={id}
                        onClick={() => handleRowClick(row)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          {no_payout_date ? moment(no_payout_date).format("YYYY-MM-DD") : ""}
                        </TableCell>
                        <TableCell>{no_payout_day || ""}</TableCell>
                        <TableCell>
                          {acquirer && acquirer !== "all"
                            ? (acquirerList.find((o) => o.id === acquirer)?.name || acquirer)
                            : "All"}
                        </TableCell>
                        <TableCell>{description || ""}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: getStatusColor(status),
                            }}
                          >
                            {status || ""}
                          </Typography>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
      {openCreateDialog && (
        <CreateDialog
          open={true}
          onClose={handleCloseDialog}
          onSuccess={() => {
            handleCloseDialog();
            queryPage(pagination, filter);
          }}
        />
      )}
      {openEditDialog && selectedId && (
        <CreateDialog
          key={selectedId}
          open={true}
          onClose={handleCloseDialog}
          selectedId={selectedId}
          initialData={editInitialData}
          onSuccess={() => {
            handleCloseDialog();
            queryPage(pagination, filter);
          }}
        />
      )}
      <Dialog open={openDeleteConfirm} onClose={() => { setOpenDeleteConfirm(false); setDeleteConfirmId(null); }}>
        <DialogTitle>Are you sure you want to delete this no payout day?</DialogTitle>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button variant="outlined" onClick={() => { setOpenDeleteConfirm(false); setDeleteConfirmId(null); }} disabled={deleteLoading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            loading={deleteLoading}
            sx={{ ...styles.customButton }}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {openViewDialog && (
        <ViewDetailDialog
          open={true}
          onClose={handleCloseDialog}
          record={selectedRecord}
          loaded={!!selectedRecord}
          approveLoading={approveLoading}
          rejectLoading={rejectLoading}
          deleteLoading={deleteLoading}
          canEdit={canEdit}
          canDelete={canDelete}
          canApproveReject={canApproveReject}
          onEdit={handleViewEdit}
          onRequestDelete={handleRequestDelete}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </Box>
    </Container>
  );
};

export default NoPayoutDayPage;
